import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import PhoneMissedRounded from '@mui/icons-material/PhoneMissedRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import RingVolumeRounded from '@mui/icons-material/RingVolumeRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import ReplyRounded from '@mui/icons-material/ReplyRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import AssignmentRounded from '@mui/icons-material/AssignmentRounded';
import PanToolRounded from '@mui/icons-material/PanToolRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import {
  EmptyState, Modal, Panel, StatusChip, Timeline, ToolbarButton, type TimelineStep,
} from '../../../ui-kit';
import { useCan } from '../../../ui/PermissionsProvider';
import { fetchMcCase, sendFirstMessage, type McCaseDetail } from '../../../services/missedCallService';
import { useLive, when } from '../rocco/live';
import { statusChip } from './status';

const MARKERS: Record<string, React.ReactNode> = {
  call_in: <RingVolumeRounded />,
  answered: <PhoneInTalkRounded />,
  missed: <PhoneMissedRounded />,
  az_missed: <AssignmentRounded />,
  held: <HourglassTopRounded />,
  whatsapp_out: <ChatRounded />,
  whatsapp_in: <ReplyRounded />,
  asked_again: <HelpOutlineRounded />,
  booked: <EventAvailableRounded />,
  needs_person: <PanToolRounded />,
  not_sent: <ErrorOutlineRounded />,
  not_delivered: <ErrorOutlineRounded />,
  az_problem: <ErrorOutlineRounded />,
};

const TONES: Record<string, TimelineStep['tone']> = {
  answered: 'good',
  missed: 'warning',
  booked: 'good',
  needs_person: 'warning',
  not_sent: 'warning',
  not_delivered: 'warning',
  az_problem: 'warning',
};

/** How far one of our messages got, in words. */
const DELIVERY: Record<string, string> = {
  queued: 'Sending', sending: 'Sending', sent: 'Sent', delivered: 'Delivered', read: 'Read',
  failed: 'Not sent', undelivered: 'Not delivered',
};

/**
 * One missed call, start to finish: the call, every WhatsApp message both
 * ways, and the callback on an agent's calendar. Watches the call while open,
 * so a reply that arrives while you are reading appears in place.
 */
export default function CaseStoryModal({ caseId, onClose }: { caseId: string; onClose: () => void }) {
  const fetcher = React.useCallback(() => fetchMcCase(caseId), [caseId]);
  const { data: found, error, reload } = useLive(fetcher, 4000);
  const canSend = useCan('manage_agents');
  const [sending, setSending] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const held = found?.status === 'message_held';
  const send = async () => {
    setSending(true);
    setProblem(null);
    try {
      await sendFirstMessage(caseId);
      await reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not send the message');
    } finally {
      setSending(false);
    }
  };

  const footer = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
      <Typography variant="caption" sx={(t) => ({ flex: 1, color: problem ? t.tokens.semantic.danger : t.tokens.text.tertiary })}>
        {problem || (held && !canSend ? 'Only someone who manages agents can send this message.' : '')}
      </Typography>
      {held && canSend ? (
        <ToolbarButton variant="brand" icon={<SendRounded />} onClick={send} loading={sending}>
          Approve and send
        </ToolbarButton>
      ) : null}
      <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
    </Stack>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={found ? (found.caller_name ? `${found.caller_name} · ${found.caller_full}` : found.caller_full) : 'Loading…'}
      subtitle={found ? `Called ${found.called_number} ${when(found.called_at)}` : undefined}
      actions={found ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          {found.agencyzoom_url ? (
            <ToolbarButton variant="ghost" icon={<OpenInNewRounded />}
              onClick={() => window.open(found.agencyzoom_url ?? '', '_blank', 'noopener')}>
              Open AgencyZoom
            </ToolbarButton>
          ) : null}
          <StatusChip {...statusChip(found.status)} />
        </Stack>
      ) : undefined}
      size="lg"
      footer={footer}
    >
      {error && !found ? <EmptyState message={error} /> : null}
      {!found && !error ? <Loading /> : null}
      {found ? <Body found={found} /> : null}
    </Modal>
  );
}

function Body({ found }: { found: McCaseDetail }) {
  const facts = [
    { label: 'Number to call', value: found.caller_full },
    { label: 'When', value: found.status === 'callback_booked' ? found.window ?? '—'
      : found.status === 'replied' ? `${found.window} (waiting for them to confirm)` : 'Not chosen yet' },
    { label: 'Agent', value: found.agent_name ?? 'Not given to anyone yet' },
    { label: 'Language', value: found.language ?? 'Not known yet' },
  ];
  if (found.caller_name) facts.splice(1, 0, { label: 'WhatsApp name', value: found.caller_name });

  return (
    <Stack spacing={2}>
      {found.status === 'needs_person' && found.needs_person_reason ? (
        <Box sx={(t) => ({
          p: 1.25, borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.semantic.warningSoft, color: t.tokens.text.primary,
        })}>
          <Typography variant="body2"><strong>Needs a person:</strong> {found.needs_person_reason}</Typography>
        </Box>
      ) : null}
      {found.status === 'message_held' && found.first_message ? (
        <Box sx={(t) => ({
          p: 1.25, borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.semantic.warningSoft, color: t.tokens.text.primary,
        })}>
          <Typography variant="body2">
            <strong>Waiting for approval.</strong> This message goes to the client when someone approves it:
            {' '}“{found.first_message}”
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="WhatsApp messages" subtitle="Everything we sent and everything they wrote, in order">
          {found.messages.length ? (
            <Stack spacing={1.25} sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}>
              {found.messages.map((m, i) => (
                <Stack key={i} spacing={0.25}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="overline"
                      sx={(t) => ({ color: m.direction === 'out' ? t.tokens.brand.primary : t.tokens.text.tertiary })}>
                      {m.direction === 'out' ? 'Us' : found.caller_name || 'Client'}
                    </Typography>
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                      {when(m.at)}{m.direction === 'out' && m.delivery ? ` · ${DELIVERY[m.delivery] ?? m.delivery}` : ''}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' })}>
                    {m.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {found.status === 'answered' ? 'None: someone on the team took the call.' : 'No messages yet.'}
            </Typography>
          )}
        </Panel>
        <Stack spacing={2}>
          <Panel title="Call back" subtitle="What the agent will see in AgencyZoom">
            <Facts rows={facts} />
          </Panel>
          <Panel title="What happened" subtitle="Each step, as it happened">
            <Timeline steps={found.steps.map((step, index) => ({
              key: `${step.kind}-${index}`,
              title: step.title,
              meta: when(step.at),
              detail: step.detail ?? undefined,
              marker: MARKERS[step.kind] ?? <ChatRounded />,
              tone: TONES[step.kind],
            }))} />
          </Panel>
        </Stack>
      </Box>
    </Stack>
  );
}

/** Labels on the left, their values on the right. Both wrap. */
function Facts({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <Stack spacing={0.75}>
      {rows.map((fact) => (
        <Stack key={fact.label} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fact.label}</Typography>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right', overflowWrap: 'anywhere' })}>
            {fact.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function Loading() {
  return (
    <Stack spacing={1.25} aria-hidden>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Skeleton variant="rounded" height={220} />
        <Skeleton variant="rounded" height={220} />
      </Box>
    </Stack>
  );
}
